<template>
  <a-modal
    title="发货"
    :visible="visible"
    @ok="handleSubmit"
    @cancel="handleCancel"
    width="1300px"
    :confirm-loading="loading"
  >
    <a-form-model :model="form" :label-col="{ span: 4 }" :wrapper-col="{ span: 18 }">
      <a-form-model-item label="要货单号">
        <span>{{ orderData.orderNo }}</span>
      </a-form-model-item>
      <a-form-model-item label="门店名称">
        <span>{{ orderData.storeName }}</span>
      </a-form-model-item>

      <a-form-model-item label="发货方式" required>
        <a-radio-group v-model="form.deliveryType" @change="handleDeliveryTypeChange">
          <a-radio :value="1">直接发货(平台库存)</a-radio>
          <a-radio :value="2">直发采购(供应商直发)</a-radio>
        </a-radio-group>
      </a-form-model-item>

      <a-form-model-item
        v-if="form.deliveryType === 2"
        label="供应商"
        required
      >
        <a-select
          v-model="form.supplierId"
          placeholder="请选择供应商"
          show-search
          :filter-option="filterOption"
          allow-clear
          style="width: 300px"
        >
          <a-select-option
            v-for="supplier in supplierList"
            :key="supplier.id"
            :value="supplier.id"
          >
            {{ supplier.supplierName }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-alert
        v-if="stockWarning"
        message="库存不足警告"
        :description="stockWarningMessage"
        type="warning"
        show-icon
        style="margin-bottom: 16px"
      />

      <a-divider>商品明细</a-divider>

      <a-table
        rowKey="id"
        :columns="columns"
        :data-source="orderData.items || []"
        :pagination="false"
        size="small"
        :scroll="{ x: 1100 }"
      >

        <span slot="shipCount" slot-scope="text, record">
          <a-input-number
            v-model="record.shipCount"
            :min="0"
            :max="record.applyCount"
            :precision="0"
            style="width: 100px"
            size="small"
            @change="calculateTotal"
          />
        </span>
        <span slot="purchaseCount" slot-scope="text, record">
          <a-input-number
            v-model="record.purchaseCount"
            :min="0"
            :max="record.applyCount - (record.shipCount || 0)"
            :precision="0"
            style="width: 100px"
            size="small"
            :disabled="form.deliveryType === 1"
            @change="calculateTotal"
          />
        </span>
      </a-table>

      <div style="margin-top: 16px; text-align: right;">
        <a-statistic
          title="发货总数"
          :value="totalShipCount"
          suffix="件"
        />
      </div>
    </a-form-model>
  </a-modal>
</template>

<script>
import { getSupplierList } from '@/api/retail/inventory'

// 仓库管理角色标识：该角色仅可查看商品名称与数量，不可查看任何金额
const WAREHOUSE_ROLE_KEYS = ['仓库管理']

export default {
  name: 'ShipModal',
  data() {
    return {
      visible: false,
      loading: false,
      orderData: {},
      form: {
        deliveryType: 1, // 1:直接发货, 2:直发采购
        supplierId: undefined, // 供应商ID
        remark: ''
      },
      supplierList: [], // 供应商列表
      totalShipCount: 0,
      stockWarning: false,
      stockWarningMessage: ''
    }
  },
  computed: {
    // 当前登录用户是否为仓库管理角色
    isWarehouseManager() {
      const roles = this.$store.getters.roles || []
      const roleList = Array.isArray(roles) ? roles : Array.from(roles)
      return WAREHOUSE_ROLE_KEYS.some(key => roleList.includes(key))
    },
    // 商品明细列：仓库管理角色只展示商品名称与数量，隐藏所有金额列
    columns() {
      const goodsColumns = [
        { title: '商品名称', dataIndex: 'goodsName', width: 140, align: 'center', ellipsis: true },
        { title: 'SKU名称', dataIndex: 'skuName', width: 110, align: 'center', ellipsis: true }
      ]
      const countColumns = [
        { title: '要货数量', dataIndex: 'applyCount', width: 80, align: 'center' },
        { title: '直接发货数量', dataIndex: 'shipCount', scopedSlots: { customRender: 'shipCount' }, width: 110, align: 'center' },
        { title: '直发采购数量', dataIndex: 'purchaseCount', scopedSlots: { customRender: 'purchaseCount' }, width: 110, align: 'center' }
      ]
      if (this.isWarehouseManager) {
        return [...goodsColumns, ...countColumns]
      }
      return [
        ...goodsColumns,
        { title: '要货数量', dataIndex: 'applyCount', width: 80, align: 'center' },
        { title: '库存', dataIndex: 'stockCount', width: 100, align: 'center' },
        { title: '采购价(元)', dataIndex: 'platformPrice', width: 90, align: 'center', customRender: (text) => `¥${text ? parseFloat(text).toFixed(2) : '0.00'}` },
        { title: '商家拿货价(元)', dataIndex: 'merchantPrice', width: 100, align: 'center', customRender: (text) => `¥${text ? parseFloat(text).toFixed(2) : '0.00'}` },
        countColumns[1],
        countColumns[2]
      ]
    }
  },
  mounted() {
    this.loadSupplierList()
  },
  methods: {
    // 加载供应商列表
    async loadSupplierList() {
      try {
        const res = await getSupplierList({ current: 1, size: 1000 })
        if (res.code == 20001 && res.data) {
          this.supplierList = res.data || []
        }
      } catch (error) {
        console.error('加载供应商列表失败:', error)
      }
    },

    // 下拉框搜索过滤
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    open(orderData) {
      this.visible = true
      this.orderData = orderData
      this.form.deliveryType = 1
      this.form.supplierId = undefined
      this.form.remark = ''

      // 初始化发货数量和采购数量
      if (this.orderData.items && this.orderData.items.length > 0) {
        this.orderData.items.forEach(item => {
          // 检查库存
          if (item.inStock) {
            item.shipCount = item.applyCount
            item.purchaseCount = 0
          } else {
            item.shipCount = item.availableStock || 0
            item.purchaseCount = item.applyCount - (item.availableStock || 0)
            this.stockWarning = true
          }
        })
      }

      this.checkStockWarning()
      this.calculateTotal()
    },

    handleDeliveryTypeChange(e) {
      const deliveryType = e.target.value

      if (deliveryType === 1) {
        // 直接发货:所有采购数量归零
        this.orderData.items.forEach(item => {
          item.purchaseCount = 0
        })
      }

      this.calculateTotal()
    },

    checkStockWarning() {
      const insufficientItems = this.orderData.items.filter(item => !item.inStock)

      if (insufficientItems.length > 0) {
        this.stockWarning = true
        const itemNames = insufficientItems.map(item => `${item.goodsName}(${item.skuName})`).join('、')
        this.stockWarningMessage = `以下商品库存不足,建议使用"直发采购"方式: ${itemNames}`
      } else {
        this.stockWarning = false
        this.stockWarningMessage = ''
      }
    },

    calculateTotal() {
      this.totalShipCount = this.orderData.items.reduce((total, item) => {
        return total + (item.shipCount || 0) + (item.purchaseCount || 0)
      }, 0)
    },

    handleCancel() {
      this.visible = false
      this.loading = false
    },

    handleSubmit() {
      // 验证
      let totalShip = 0
      let totalPurchase = 0

      this.orderData.items.forEach(item => {
        totalShip += (item.shipCount || 0)
        totalPurchase += (item.purchaseCount || 0)
      })

      if (totalShip === 0 && totalPurchase === 0) {
        this.$message.error('请至少设置发货或采购数量')
        return
      }

      // 直发采购时必须选择供应商
      if (totalPurchase > 0 && !this.form.supplierId) {
        this.$message.error('直发采购请选择供应商')
        return
      }

      const shipItems = this.orderData.items.map(item => ({
        itemId: item.id,
        goodsId: item.goodsId,
        skuId: item.skuId,
        shipCount: item.shipCount || 0,
        purchaseCount: item.purchaseCount || 0
      }))

      this.loading = true

      // 调用父组件的发货方法
      this.$emit('submit', {
        orderId: this.orderData.id,
        deliveryType: this.form.deliveryType,
        supplierId: this.form.supplierId,
        items: shipItems,
        remark: this.form.remark
      })
    }
  }
}
</script>

<style lang="less" scoped>
</style>
